import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ContractorAccount {
  contractorUserId: string;
  contractorId: string;
  tenantId: string;
  tenantName: string;
  contractorCompanyName: string;
  firstName: string;
  lastName: string;
  email: string;
  isPrimaryContact: boolean;
  canManageJobs: boolean;
  canViewBilling: boolean;
  /** Whether the contractor needs to set up a password (first magic link login) */
  needsPasswordSetup: boolean;
}

interface ContractorAuthState {
  // Hydration state
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;

  // Auth state
  isAuthenticated: boolean;
  accessToken: string | null;
  // SECURITY: refreshToken is now stored in HttpOnly cookie, not in JS-accessible storage
  // This property is kept for backward compatibility but always returns null
  refreshToken: string | null;
  expiresAt: Date | null;
  account: ContractorAccount | null;

  // Actions
  // Note: refreshToken parameter kept for backward compatibility but is ignored
  setAuth: (
    accessToken: string,
    refreshToken: string,
    expiresAt: Date,
    account: ContractorAccount
  ) => void;
  clearAuth: () => void;
  isTokenExpired: () => boolean;
  updateAccessToken: (accessToken: string, expiresAt: Date) => void;
  /** Mark password setup as complete */
  markPasswordSetupComplete: () => void;
}

/**
 * Contractor Portal authentication store using Zustand with persistence
 */
export const useContractorStore = create<ContractorAuthState>()(
  persist(
    (set, get) => ({
      // Hydration state - tracks whether localStorage has been loaded
      _hasHydrated: false,
      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),

      // Initial state
      isAuthenticated: false,
      accessToken: null,
      // SECURITY: Refresh token is now in HttpOnly cookie - not accessible from JavaScript
      refreshToken: null,
      expiresAt: null,
      account: null,

      // Note: refreshToken parameter kept for backward compatibility but is ignored
      // SECURITY: Refresh token is now stored in HttpOnly cookie by the server
      setAuth: (accessToken, _refreshToken, expiresAt, account) => {
        set({
          isAuthenticated: true,
          accessToken,
          refreshToken: null, // SECURITY: Never store in JS-accessible state
          expiresAt,
          account,
          _hasHydrated: true, // Also mark as hydrated when setting auth directly
        });
      },

      clearAuth: () => {
        // SECURITY: Refresh token cookie is cleared by the server via /contractor-portal/logout endpoint
        set({
          isAuthenticated: false,
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          account: null,
        });
      },

      isTokenExpired: () => {
        const { expiresAt } = get();
        if (!expiresAt) return true;

        // Consider token expired 1 minute before actual expiry
        // Handle both Date objects and ISO strings (after rehydration from localStorage)
        const expiryDate = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);

        // Check for invalid date
        if (isNaN(expiryDate.getTime())) return true;

        const bufferMs = 60 * 1000;
        return new Date().getTime() > expiryDate.getTime() - bufferMs;
      },

      updateAccessToken: (accessToken, expiresAt) => {
        set({ accessToken, expiresAt });
      },

      markPasswordSetupComplete: () => {
        const { account } = get();
        if (account) {
          set({
            account: { ...account, needsPasswordSetup: false },
          });
        }
      },
    }),
    {
      name: 'contractor-auth-storage',
      // SECURITY: Don't persist refreshToken - it's now in HttpOnly cookie
      partialize: (state) => ({
        accessToken: state.accessToken,
        // refreshToken intentionally omitted - stored in HttpOnly cookie
        expiresAt: state.expiresAt,
        account: state.account,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Called when hydration is complete
        state?.setHasHydrated(true);
      },
    }
  )
);
